// Labels on the board's sideways-scrolling track, at the edges of what shows (QA-765): the names
// column held still on the left and the board's own edge on the right cut whatever crosses them, so a
// label is drawn whole or not at all. An hour's label whose hour still shows slides in beside the edge
// ("inset"); one whose hour has gone under the edge goes. A time-away band's words ("Off") sit at the
// start of the part of the band that shows, or go when that part is too narrow for them. Pure: the
// Board and the TimeScale measure, these decide.

/** A box along the track, in pixels from the same origin (the viewport's). */
export interface Span {
  left: number;
  right: number;
}

/** What to do with a label: leave it, slide it by `shift` pixels, or hide it ('cut'). */
export type EdgeFit = { shift: number } | 'cut';

const NONE: EdgeFit = { shift: 0 };

/**
 * An hour's label (or now's) at `anchor`, the point it marks, drawn at `label` within `view`: whole
 * inside, it stays; crossing an edge while its anchor still shows, it slides in to `pad` from that
 * edge; its anchor gone under the edge, or too wide for the view, it is cut.
 */
export function hourFit(label: Span, anchor: number, view: Span, pad = 0): EdgeFit {
  if (label.left >= view.left && label.right <= view.right) return NONE;
  const width = label.right - label.left;
  if (anchor < view.left || anchor > view.right || width + 2 * pad > view.right - view.left) return 'cut';
  if (label.left < view.left) return { shift: view.left + pad - label.left };
  return { shift: view.right - pad - label.right };
}

/**
 * A band's words (time away: "Off", "Dentist") drawn at `label`, inside `band`, within `view`: whole
 * where they are, they stay; otherwise they move to the start of the part of the band that shows,
 * `pad` in, when they fit there, and are cut when they don't.
 */
export function bandFit(label: Span, band: Span, view: Span, pad = 0): EdgeFit {
  if (label.left >= view.left && label.right <= view.right) return NONE;
  const from = Math.max(band.left, view.left);
  const to = Math.min(band.right, view.right);
  const width = label.right - label.left;
  if (to - from < width + pad) return 'cut';
  return { shift: from + (from > band.left ? pad : 0) - label.left };
}

/** Applies a fit to an element: `translate` for a shift, data-edge-cut (hidden) for a cut. */
export function applyFit(el: HTMLElement, fit: EdgeFit): void {
  if (fit === 'cut') {
    el.style.translate = '';
    el.setAttribute('data-edge-cut', '');
    return;
  }
  el.removeAttribute('data-edge-cut');
  el.style.translate = fit.shift ? `${Math.round(fit.shift)}px 0` : '';
}

/** Takes a fit off, so the element can be measured where it's drawn. */
export function clearFit(el: HTMLElement): void {
  el.style.translate = '';
  el.removeAttribute('data-edge-cut');
}

/** What of the board's track shows: from the names column's edge to the board's own. */
export function trackView(board: HTMLElement): Span {
  const b = board.getBoundingClientRect();
  const corner = board.querySelector<HTMLElement>('.fl-scale__corner, .fl-lane__head');
  const left = corner ? corner.getBoundingClientRect().right : b.left;
  return { left, right: b.left + board.clientLeft + board.clientWidth };
}
